import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { useActivePromo } from "@/lib/promo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Check, Copy, Gift, X } from "lucide-react";

const SEEN_KEY = "iv_entry_popup_until";
const HOLD_DAYS = 7;
const DELAY_MS = 6000;

const snoozed = () => {
  const until = Number(localStorage.getItem(SEEN_KEY) || 0);
  return until > Date.now();
};

const snooze = () => {
  localStorage.setItem(SEEN_KEY, String(Date.now() + HOLD_DAYS * 24 * 60 * 60 * 1000));
};

/**
 * Homepage welcome offer. Appears once per visitor — after a short delay or the
 * first scroll, whichever comes first — and only while a promo is actually live,
 * so it never advertises a code that checkout would refuse. The email is saved to
 * the newsletter list and the code is revealed in exchange.
 */
export const EntryPopup = () => {
  const promo = useActivePromo();
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [busy, setBusy] = useState(false);
  const [revealed, setRevealed] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!promo || snoozed()) return undefined;
    let done = false;
    const show = () => {
      if (done) return;
      done = true;
      setOpen(true);
      window.removeEventListener("scroll", show);
    };
    const timer = setTimeout(show, DELAY_MS);
    window.addEventListener("scroll", show, { passive: true });
    return () => { clearTimeout(timer); window.removeEventListener("scroll", show); };
  }, [promo]);

  if (!promo || !open) return null;

  const close = () => { snooze(); setOpen(false); };

  const submit = async (e) => {
    e.preventDefault();
    if (!/^\S+@\S+\.\S+$/.test(email)) return toast.error("Please enter a valid email address");
    setBusy(true);
    try {
      await api.post("/marketing/subscribe", {
        email, phone, source: "entry_popup", promo_code: promo.code,
      });
      snooze();
      setRevealed(true);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setBusy(false); }
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(promo.code);
      setCopied(true);
      toast.success("Code copied");
    } catch {
      toast.info(`Use code ${promo.code} at checkout`);
    }
    // The visitor has what they came for — get out of their way.
    setTimeout(close, 900);
  };

  // Selecting the code by hand and copying it counts just the same.
  const onManualCopy = () => {
    toast.success("Code copied");
    setTimeout(close, 600);
  };

  return (
    <div
      className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center bg-foreground/40 backdrop-blur-[2px] px-4 pb-6 sm:pb-0"
      role="dialog"
      aria-modal="true"
      aria-label="Welcome offer"
      onClick={close}
      data-testid="entry-popup-overlay"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="relative w-full max-w-md rounded-[1.75rem] border border-border bg-paper p-8 shadow-[0_30px_80px_-40px_rgba(33,80,52,0.65)] fade-in"
        data-testid="entry-popup"
      >
        <button
          onClick={close}
          aria-label="Close"
          data-testid="entry-popup-close"
          className="absolute right-4 top-4 p-1.5 rounded-full text-muted-foreground hover:text-primary hover:bg-primary/10 transition-colors"
        >
          <X size={16} />
        </button>

        <span className="w-11 h-11 rounded-full bg-secondary/15 text-secondary flex items-center justify-center">
          <Gift size={18} />
        </span>

        {revealed ? (
          <>
            <h3 className="font-display text-3xl mt-5" data-testid="entry-popup-thanks">Dhanyavaad!</h3>
            <p className="text-sm text-muted-foreground mt-2">
              Your code is ready — use it at checkout on any order.
            </p>
            <button
              onClick={copy}
              onCopy={onManualCopy}
              data-testid="entry-popup-code"
              className="mt-5 w-full flex items-center justify-between gap-3 rounded-2xl border border-dashed border-secondary/60 bg-secondary/10 px-5 py-4 hover:bg-secondary/15 transition-colors select-text"
            >
              <span className="font-display text-2xl tracking-[0.18em] text-secondary">{promo.code}</span>
              <span className="text-xs text-muted-foreground inline-flex items-center gap-1.5">
                {copied ? <><Check size={13} /> Copied</> : <><Copy size={13} /> Tap to copy</>}
              </span>
            </button>
            <p className="text-xs text-muted-foreground mt-3" data-testid="entry-popup-benefit">{promo.benefit}</p>
          </>
        ) : (
          <>
            <div className="overline mt-5">Welcome offer</div>
            <h3 className="font-display text-3xl mt-1.5 leading-tight" data-testid="entry-popup-title">
              {promo.benefit}
            </h3>
            <p className="text-sm text-muted-foreground mt-3">
              Leave your email and we will reveal the code — plus first word on new remedies and
              seasonal offers from our family practice.
            </p>
            <form onSubmit={submit} className="mt-6 space-y-3">
              <Input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@email.com"
                className="h-12 bg-background"
                data-testid="entry-popup-email"
              />
              <Input
                type="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="Phone (optional — for WhatsApp offers)"
                className="h-12 bg-background"
                data-testid="entry-popup-phone"
              />
              <Button type="submit" disabled={busy} className="btn-action h-12 w-full" data-testid="entry-popup-submit">
                {busy ? "Just a moment…" : "Reveal my code"}
              </Button>
            </form>
            <button
              onClick={close}
              className="text-xs text-muted-foreground hover:text-primary mt-4 underline"
              data-testid="entry-popup-dismiss"
            >
              No thanks, I'll browse first
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export default EntryPopup;
